import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompanies } from '../hooks/useCompanies';
import { useDashboardStats, useProfileCoverage, type ProfileCoverage } from '../hooks/useDashboardStats';
import { Company } from '../types';

function formatVolume(eur: number): string {
  if (!eur) return '—';
  if (eur >= 1_000_000_000) return `${(eur / 1_000_000_000).toFixed(1).replace(/\.0$/, '')} Mrd. €`;
  if (eur >= 1_000_000) return `${(eur / 1_000_000).toFixed(1).replace(/\.0$/, '')} Mio. €`;
  return `${(eur / 1_000).toFixed(0)} Tsd. €`;
}

function KpiCard({
  label, value, sub, accent, color, onClick,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: boolean;
  color?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className="card"
      style={{ padding: '20px 24px', cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    >
      <div style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{
        fontSize: '28px', fontWeight: 600, lineHeight: 1.2,
        fontFamily: 'DM Mono, monospace',
        color: color ?? (accent ? 'var(--accent)' : 'var(--text)'),
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

function MachCard({ stats }: { stats: ReturnType<typeof useDashboardStats>['data'] }) {
  const navigate = useNavigate();
  const total = stats?.mach_total ?? '—';
  const inArbeit = stats?.mach_in_arbeit ?? '—';
  const neu = stats?.mach_neu ?? '—';

  return (
    <div className="card" style={{ padding: '20px 24px', cursor: 'pointer' }} onClick={() => navigate('/verkaufsmandate')}>
      <div style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        MACH — Investorenmatches
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', marginBottom: '12px' }}>
        <div style={{ fontSize: '28px', fontWeight: 600, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>
          {total}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text3)', paddingBottom: '3px' }}>gesamt</div>
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        <div style={{
          flex: 1, padding: '8px 12px', borderRadius: 'var(--radius)',
          background: '#fff3e0',
        }}>
          <div style={{ fontSize: '11px', color: '#e65100', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>In Arbeit</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#e65100', fontFamily: 'DM Mono, monospace' }}>{inArbeit}</div>
        </div>
        <div style={{
          flex: 1, padding: '8px 12px', borderRadius: 'var(--radius)',
          background: '#e8eaf6',
        }}>
          <div style={{ fontSize: '11px', color: '#3949ab', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>Neu</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#3949ab', fontFamily: 'DM Mono, monospace' }}>{neu}</div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, color = 'var(--text)', bg }: {
  label: string; value: number | string; sub?: string; color?: string; bg?: string;
}) {
  return (
    <div style={{ padding: '12px 14px', background: bg ?? 'var(--surface2)', borderRadius: 'var(--radius)' }}>
      <div style={{ fontSize: '10px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: 'DM Mono, monospace', color, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '10px', color: 'var(--text3)', marginTop: '3px' }}>{sub}</div>}
    </div>
  );
}

function DataQualityCard({ coverage }: { coverage: ProfileCoverage | undefined }) {
  if (!coverage) {
    return (
      <div className="card">
        <div className="card-header"><span className="card-title">Datenqualität</span></div>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>Lädt…</div>
      </div>
    );
  }

  const total = coverage.investors_total || 1;
  const pct = (n: number) => `${((n / total) * 100).toFixed(1)} %`;
  const maxBucket = Math.max(1, ...coverage.profile_histogram.map(b => b.investors));
  const maxCountry = Math.max(1, ...(coverage.investors_by_country ?? []).map(c => c.investors));

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Datenqualität</span></div>

      <div style={{ padding: '16px 20px 4px', fontSize: '11px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
        Investoren ({coverage.investors_total.toLocaleString('de-DE')})
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', padding: '4px 20px 12px' }}>
        <StatTile label="Mit Website" value={coverage.investors_with_website.toLocaleString('de-DE')} sub={pct(coverage.investors_with_website)} color="var(--accent)" />
        <StatTile label="Ohne Website" value={coverage.investors_without_website.toLocaleString('de-DE')} sub={`${pct(coverage.investors_without_website)} • HR-Lookup`} color="#e65100" bg="#fff3e0" />
        <StatTile label="Mit Investorenart" value={coverage.investors_with_investor_type.toLocaleString('de-DE')} sub={pct(coverage.investors_with_investor_type)} color="var(--accent)" />
        <StatTile label="Ohne Investorenart" value={coverage.investors_without_investor_type.toLocaleString('de-DE')} sub={`${pct(coverage.investors_without_investor_type)} • offen`} color="#e65100" bg="#fff3e0" />
      </div>

      <div style={{ padding: '8px 20px 4px', fontSize: '11px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
        Profile ({coverage.profiles_total_active.toLocaleString('de-DE')} aktiv)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', padding: '4px 20px 12px' }}>
        <StatTile label="Mit Profil" value={coverage.investors_with_profile.toLocaleString('de-DE')} sub={`${pct(coverage.investors_with_profile)} Investoren`} color="var(--accent)" />
        <StatTile label="Aktive Profile" value={coverage.profiles_total_active.toLocaleString('de-DE')} sub="gesamt" />
        <StatTile label="Freigegeben" value={coverage.profiles_approved.toLocaleString('de-DE')} sub={coverage.profiles_total_active > 0 ? `${((coverage.profiles_approved / coverage.profiles_total_active) * 100).toFixed(1)} %` : '—'} color="#2e7d32" bg="#e8f5e9" />
        <StatTile label="Review-Backlog" value={coverage.profiles_review_backlog.toLocaleString('de-DE')} sub="ungeprüft" color="#e65100" bg="#fff3e0" />
      </div>

      <div style={{ padding: '8px 20px 4px', fontSize: '11px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
        Kontakte ({coverage.contacts_total.toLocaleString('de-DE')} gesamt)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', padding: '4px 20px 16px' }}>
        <StatTile label="Mit Haupt-AP" value={coverage.investors_with_hauptansprechpartner.toLocaleString('de-DE')} sub={`${pct(coverage.investors_with_hauptansprechpartner)} versandfähig`} color="var(--accent)" />
        <StatTile label="Mit ≥1 Kontakt" value={coverage.investors_with_any_contact.toLocaleString('de-DE')} sub={pct(coverage.investors_with_any_contact)} />
        <StatTile label="Ohne Kontakt" value={coverage.investors_without_any_contact.toLocaleString('de-DE')} sub={pct(coverage.investors_without_any_contact)} color="#e65100" bg="#fff3e0" />
        <StatTile label="Kontakte Review" value={coverage.contacts_review_backlog.toLocaleString('de-DE')} sub="ungeprüft" color="#e65100" bg="#fff3e0" />
      </div>

      <div style={{ padding: '8px 20px 16px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
          Profil-Histogramm (aktive Ankaufsprofile pro Investor)
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {coverage.profile_histogram.map(b => (
            <div key={b.bucket} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '32px', fontSize: '11px', color: 'var(--text3)', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>
                {b.bucket}
              </div>
              <div style={{ flex: 1, position: 'relative', height: '16px', background: 'var(--surface2)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${(b.investors / maxBucket) * 100}%`,
                  background: b.bucket === '0' ? '#cfd8dc' : 'var(--accent)',
                  transition: 'width 200ms ease',
                }} />
              </div>
              <div style={{ width: '60px', fontSize: '11px', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>
                {b.investors}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '8px 20px 12px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
          Coverage pro Investorenart
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Investorenart</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Total</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Mit Profil</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Profil %</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Mit Kontakt</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Mit Haupt-AP</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Haupt %</th>
            </tr>
          </thead>
          <tbody>
            {coverage.coverage_by_investor_type.map(row => (
              <tr key={row.type} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 8px' }}>{row.type}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{row.total.toLocaleString('de-DE')}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: row.with_profile > 0 ? 'var(--accent)' : 'var(--text3)' }}>
                  {row.with_profile}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--text3)' }}>
                  {row.total > 0 ? `${(row.with_profile / row.total * 100).toFixed(1)}%` : '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: row.with_any_contact > 0 ? 'var(--accent)' : 'var(--text3)' }}>
                  {row.with_any_contact}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace', color: row.with_haupt > 0 ? 'var(--accent)' : 'var(--text3)' }}>
                  {row.with_haupt}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--text3)' }}>
                  {row.total > 0 ? `${(row.with_haupt / row.total * 100).toFixed(1)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {coverage.investors_by_country && coverage.investors_by_country.length > 0 && (
        <div style={{ padding: '8px 20px 20px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
            Top-Länder (Investoren)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {coverage.investors_by_country.slice(0, 10).map(c => (
              <div key={c.country} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '36px', fontSize: '11px', color: 'var(--text3)', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>
                  {c.country}
                </div>
                <div style={{ flex: 1, position: 'relative', height: '14px', background: 'var(--surface2)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(c.investors / maxCountry) * 100}%`, background: c.country === '—' ? '#cfd8dc' : 'var(--accent)' }} />
                </div>
                <div style={{ width: '60px', fontSize: '11px', fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>
                  {c.investors.toLocaleString('de-DE')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: stats } = useDashboardStats();
  const { data: coverage } = useProfileCoverage();
  const { data: recentCompanies } = useCompanies({ limit: 10, is_buyer: true, sort_by: 'match_count', sort_dir: 'desc' });

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header-title">Dashboard</div>
          <div className="page-header-sub">Überblick</div>
        </div>
      </div>

      <div className="content">
        {/* KPI Row */}
        <div className="kpi-grid">

          {/* Investoren gesamt — mit Unterkacheln Nicht geprüft / Nicht freigegeben */}
          <div className="card" style={{ padding: '20px 24px', cursor: 'pointer' }} onClick={() => navigate('/companies')}>
            <div style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Investoren gesamt
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', marginBottom: '12px' }}>
              <div style={{ fontSize: '28px', fontWeight: 600, fontFamily: 'DM Mono, monospace', lineHeight: 1, color: 'var(--text)' }}>
                {stats?.investors_total ?? '—'}
              </div>
              {stats?.profiles_total != null && (
                <div style={{ fontSize: '12px', color: 'var(--text3)', paddingBottom: '3px' }}>
                  Ankaufsprofile: {stats.profiles_total}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {/* Nicht geprüft */}
              <div style={{
                flex: 1, padding: '8px 12px', borderRadius: 'var(--radius)',
                background: (stats?.investors_not_verified ?? 0) > 0 ? '#fff8e1' : 'var(--surface2)',
              }}>
                <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px',
                  color: (stats?.investors_not_verified ?? 0) > 0 ? '#F59E0B' : 'var(--text3)' }}>
                  Nicht geprüft
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: 'DM Mono, monospace',
                  color: (stats?.investors_not_verified ?? 0) > 0 ? '#F59E0B' : 'var(--text3)' }}>
                  {stats?.investors_not_verified ?? 0}
                </div>
              </div>
              {/* Nicht freigegeben */}
              <div style={{
                flex: 1, padding: '8px 12px', borderRadius: 'var(--radius)',
                background: (stats?.investors_not_approved ?? 0) > 0 ? '#fff8e1' : 'var(--surface2)',
              }}>
                <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px',
                  color: (stats?.investors_not_approved ?? 0) > 0 ? '#F59E0B' : 'var(--text3)' }}>
                  Nicht freigegeben
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: 'DM Mono, monospace',
                  color: (stats?.investors_not_approved ?? 0) > 0 ? '#F59E0B' : 'var(--text3)' }}>
                  {stats?.investors_not_approved ?? 0}
                </div>
              </div>
            </div>
          </div>

          <KpiCard
            label="Verkaufsmandate gesamt"
            value={stats?.mandates_total ?? '—'}
            sub={stats?.mandates_active != null ? `Aktiv: ${stats.mandates_active}` : undefined}
            onClick={() => navigate('/verkaufsmandate')}
          />

          <KpiCard
            label="Verkaufsvolumen gesamt"
            value={stats?.mandates_volume_total_eur != null ? formatVolume(stats.mandates_volume_total_eur) : '—'}
            sub={stats?.mandates_volume_active_eur != null ? `Aktive Mandate: ${formatVolume(stats.mandates_volume_active_eur)}` : undefined}
            onClick={() => navigate('/verkaufsmandate')}
          />

          <MachCard stats={stats} />
        </div>

        {/* Datenqualität */}
        <DataQualityCard coverage={coverage} />

        {/* Recent investors */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Aktive Investoren</span>
            <button className="btn" onClick={() => navigate('/companies')}>
              Alle anzeigen
            </button>
          </div>
          {recentCompanies?.data && recentCompanies.data.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {recentCompanies.data.map((company: Company, idx: number) => (
                  <tr
                    key={company.id}
                    onClick={() => navigate(`/companies/${company.id}`)}
                    style={{ borderTop: idx > 0 ? '1px solid var(--border)' : undefined, cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: '12px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {(() => {
                          const src = company.logo_url ||
                            (company.website ? (() => { try { return `https://www.google.com/s2/favicons?domain=${new URL(company.website!).hostname}&sz=64`; } catch { return null; } })() : null);
                          return src ? (
                            <img src={src} alt=""
                              style={{ width: 20, height: 20, borderRadius: 3, flexShrink: 0, objectFit: 'contain' }}
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : null;
                        })()}
                        <div>
                          <div style={{ fontWeight: 500, fontSize: '13px' }}>{company.display_name}</div>
                          {company.city && (
                            <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>
                              {company.city}{company.country_code ? `, ${company.country_code}` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {(company.match_count ?? 0) > 0 && (
                          <span style={{
                            padding: '2px 8px',
                            background: '#e8f5e9', color: '#2e7d32',
                            borderRadius: '20px', fontSize: '11px', fontWeight: 600, fontFamily: 'DM Mono, monospace',
                          }}>
                            {company.match_count} MACH
                          </span>
                        )}
                        <span style={{
                          padding: '2px 8px',
                          background: (company.profile_count ?? 0) > 0 ? 'var(--accent-light)' : 'var(--surface2)',
                          color: (company.profile_count ?? 0) > 0 ? 'var(--accent)' : 'var(--text3)',
                          borderRadius: '20px', fontSize: '11px', fontFamily: 'DM Mono, monospace',
                        }}>
                          {company.profile_count ?? 0} Profile
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>
              Noch keine Investoren vorhanden.
            </div>
          )}
        </div>
      </div>
    </>
  );
}
